import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { validateGuestForm } from "../../../../utils/formValidation";

export const MyGuestListPopup = ({
  isOpen,
  onClose,
  title,
  customerId,
  setSelectedGuests,
  selectedGuests,
  guestCount,
}) => {
  const popupRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    firstName: "",
    lastName: "",
    gender: "",
    dob: "",
    passport: "",
  });
  const [errors, setErrors] = useState({
    title: "",
    firstName: "",
    lastName: "",
    gender: "",
    dob: "",
    passport: "",
  });
  const [guestList, setGuestList] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  const updateFormField = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => ({ ...p, [key]: "" }));
  };

  const handleAdd = () => {
    if (validateGuestForm(form, setErrors)) {
      const apiBody = {
        title: form.title,
        firstName: form.firstName,
        lastName: form.lastName,
        gender: form.gender,
        dob: form.dob,
        passportNo: form.passport,
        customerId: customerId,
      };
      callAddGuestApi(apiBody);
    }
  };

  const callAddGuestApi = async (apiBody) => {
    setLoading(true);
    const apiUrl = "/dial-api/encalm/guest";
    // console.log("apiBody", apiBody);
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(apiBody),
      });
      const result = await response.json();
      // console.log("result", result);
      if (result.status === "ok") {
        setForm({
          title: "",
          firstName: "",
          lastName: "",
          gender: "",
          dob: "",
          passport: "",
        });
        fetchGuest();
        setLoading(false);
      }
    } catch (error) {
      console.error("Error calling API:", error);
    }
  };

  const fetchGuest = async () => {
    const apiUrl = `/dial-api/encalm/guestlist?customerId=${customerId}`;
    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const result = await response.json();
      if (result.status === "ok") {
        setGuestList(result.guestList);
        return result.guestList;
      }
    } catch (error) {
      console.error("Error calling API:", error);
    }
  };

  const toggleSelect = (guest) => {
    const exists = selectedGuests.some((x) => x.Id === guest.Id);

    if (exists) {
      // remove guest
      setSelectedGuests((prev) => prev.filter((x) => x.Id !== guest.Id));
    } else {
      if (selectedGuests.length >= parseInt(guestCount)) {
        alert(`You can select up to ${guestCount} guests only.`);
        return;
      }
      // add guest
      setSelectedGuests((prev) => [...prev, guest]);
    }
  };

  const handleEditClick = (guest) => {
    // console.log("guest", guest);
    setEditingId(guest.Id);
    setEditData({
      first_name: guest.first_name,
      last_name: guest.last_name,
      gender: guest.gender,
      dob: guest.dob,
      passportNo: guest.passportNo,
    });
  };

  const handleSaveEdit = (id) => {
    setGuestList((prev) =>
      prev.map((g) => (g.Id === id ? { ...g, ...editData } : g))
    );
    setEditingId(null);
  };

  const handleClosePopup = () => {
    setForm({
      title: "",
      firstName: "",
      lastName: "",
      gender: "",
      dob: "",
      passport: "",
    });
    setSelectedGuests([]);
    setEditingId(null);
    onClose();
  };

  const handleSelectedGuests = () => {
    if (selectedGuests.length === 0) {
      alert("Please select at least one guest.");
      return;
    }
    onClose();
  };

  useEffect(() => {
    fetchGuest();

    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!isOpen) return null;

  // console.log("selectedGuse", selectedGuests);

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50 ">
      <div
        ref={popupRef}
        className="bg-white rounded-lg w-[90%] md:w-[80%] xl:w-[70%] p-[2vw]  relative"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-[2vw]">
          <h2 className="font-redhatBold text-primary text-[4.5vw] md:text-[3vw] xl:text-[2vw]">
            My Guest List
          </h2>
          <button
            onClick={handleClosePopup}
            className="text-primary text-[6vw] md:text-[4vw] xl:text-[3vw]"
          >
            ×
          </button>
        </div>
        <hr className="mb-4" />

        {/* Input Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
          {/* --- Form Fields (unchanged) --- */}
          {/* Title */}
          <div>
            <label className="font-redhatMedium text-primary text-[3.5vw] md:text-[2.2vw] xl:text-[1.1vw]">
              Title*
            </label>
            <select
              value={form.title}
              onChange={(e) => updateFormField("title", e.target.value)}
              className="w-full border border-primary text-primary rounded-md px-[1.5vw] xl:px-[1vw] py-[1.5vw] xl:py-[0.5vw] mt-[0.5vw] font-redhatRegular"
            >
              <option value="">Select</option>
              {title &&
                title?.map((el, ind) => (
                  <option key={el.id} value={el.id}>
                    {el.name}
                  </option>
                ))}
            </select>
            {errors.title && (
              <p className="text-red-500 text-[3vw] md:text-[1.5vw] xl:text-[1vw] font-redhatRegular">
                {errors.title}
              </p>
            )}
          </div>

          {/* First Name */}
          <div>
            <label className="font-redhatMedium text-primary text-[3.5vw] md:text-[2.2vw] xl:text-[1.1vw]">
              First Name*
            </label>
            <input
              value={form.firstName}
              onChange={(e) => updateFormField("firstName", e.target.value)}
              placeholder="Enter Name"
              className="w-full border border-primary text-primary rounded-md px-[1.5vw] xl:px-[1vw] py-[1.5vw] xl:py-[0.5vw] mt-[0.5vw] font-redhatRegular"
            />
            {errors.firstName && (
              <p className="text-red-500 text-[3vw] md:text-[1.5vw] xl:text-[1vw] font-redhatRegular">
                {errors.firstName}
              </p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="font-redhatMedium text-primary text-[3.5vw] md:text-[2.2vw] xl:text-[1.1vw]">
              Last Name*
            </label>
            <input
              value={form.lastName}
              onChange={(e) => updateFormField("lastName", e.target.value)}
              placeholder="Enter Name"
              className="w-full border border-primary text-primary rounded-md px-[1.5vw] xl:px-[1vw] py-[1.5vw] xl:py-[0.5vw] mt-[0.5vw] font-redhatRegular"
            />
            {errors.lastName && (
              <p className="text-red-500 text-[3vw] md:text-[1.5vw] xl:text-[1vw] font-redhatRegular">
                {errors.lastName}
              </p>
            )}
          </div>

          {/* Gender */}
          <div>
            <label className="font-redhatMedium text-primary text-[3.5vw] md:text-[2.2vw] xl:text-[1.1vw]">
              Gender*
            </label>
            <select
              value={form.gender}
              onChange={(e) => updateFormField("gender", e.target.value)}
              className="w-full border border-primary text-primary rounded-md px-[1.5vw] xl:px-[1vw] py-[1.5vw] xl:py-[0.5vw] mt-[0.5vw] font-redhatRegular"
            >
              <option value="">Select</option>
              <option>Male</option>
              <option>Female</option>
              <option>Other</option>
            </select>
            {errors.gender && (
              <p className="text-red-500 text-[3vw] md:text-[1.5vw] xl:text-[1vw] font-redhatRegular">
                {errors.gender}
              </p>
            )}
          </div>

          {/* DOB */}
          <div>
            <label className="font-redhatMedium text-primary text-[3.5vw] md:text-[2.2vw] xl:text-[1.1vw]">
              Date of Birth*
            </label>
            <DatePicker
              selected={
                form.dob
                  ? new Date(form.dob.split("-").reverse().join("-"))
                  : null
              }
              onChange={(date) =>
                updateFormField(
                  "dob",
                  date
                    ? date.toLocaleDateString("en-GB").replaceAll("/", "-")
                    : ""
                )
              }
              dateFormat="yyyy-MM-dd"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              maxDate={new Date()}
              placeholderText="Select DOB"
              className="w-full border border-primary text-primary rounded-md px-[1.5vw] xl:px-[1vw] py-[1.5vw] xl:py-[0.5vw] mt-[0.5vw] font-redhatRegular"
            />
            {errors.dob && (
              <p className="text-red-500 text-[3vw] md:text-[1.5vw] xl:text-[1vw] font-redhatRegular">
                {errors.dob}
              </p>
            )}
          </div>

          {/* Passport */}
          <div>
            <label className="font-redhatMedium text-primary text-[3.5vw] md:text-[2.2vw] xl:text-[1.1vw]">
              Passport/Govt ID*
            </label>
            <input
              value={form.passport}
              onChange={(e) => updateFormField("passport", e.target.value)}
              placeholder="Enter ID"
              className="w-full border border-primary text-primary rounded-md px-[1.5vw] xl:px-[1vw] py-[1.5vw] xl:py-[0.5vw] mt-[0.5vw] font-redhatRegular"
            />
            {errors.passport && (
              <p className="text-red-500 text-[3vw] md:text-[1.5vw] xl:text-[1vw] font-redhatRegular">
                {errors.passport}
              </p>
            )}
          </div>
        </div>

        <button
          onClick={handleAdd}
          className="bg-primary text-white px-[2.5vw] xl:px-[1.5vw] py-[1vw] xl:py-[0.5vw] rounded font-redhatMedium mb-[3vw]"
        >
          {loading ? "Adding Guest..." : "+ Add"}
        </button>

        {/* Guest List Table */}
        {guestList.length > 0 && (
          <div className="overflow-x-auto overflow-y-auto max-h-[40vh] mb-4">
            <table className="w-full border text-left">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 border">Select</th>
                  <th className="p-2 border">Name</th>
                  <th className="p-2 border">Gender</th>
                  <th className="p-2 border">Date of Birth</th>
                  <th className="p-2 border">PassportNo/Govt ID</th>
                  <th className="p-2 border">Edit</th>
                </tr>
              </thead>
              <tbody>
                {guestList.map((guest, ind) => (
                  <tr key={guest.id} className="border-b">
                    <td className="p-2 border text-center">
                      <input
                        type="checkbox"
                        checked={selectedGuests.some((x) => x.Id === guest.Id)}
                        onChange={() => toggleSelect(guest)}
                      />
                    </td>

                    {/* Editable / Non-editable Cells */}
                    <td className="p-2 border">
                      {editingId === guest.Id ? (
                        <input
                          value={editData.first_name}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              first_name: e.target.value,
                            })
                          }
                          className="border p-1 w-full"
                        />
                      ) : (
                        `${guest.first_name} ${guest.last_name}`
                      )}
                    </td>
                    <td className="p-2 border">
                      {editingId === guest.Id ? (
                        <select
                          value={editData.gender}
                          onChange={(e) =>
                            setEditData({ ...editData, gender: e.target.value })
                          }
                          className="border p-1 w-full"
                        >
                          <option>Male</option>
                          <option>Female</option>
                          <option>Other</option>
                        </select>
                      ) : (
                        guest.gender
                      )}
                    </td>
                    <td className="p-2 border">
                      {editingId === guest.Id ? (
                        <input
                          type="text"
                          value={editData.dob}
                          onChange={(e) =>
                            setEditData({ ...editData, dob: e.target.value })
                          }
                          className="border p-1 w-full"
                        />
                      ) : (
                        guest.dob
                      )}
                    </td>
                    <td className="p-2 border">
                      {editingId === guest.Id ? (
                        <input
                          value={editData.passportNo}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              passportNo: e.target.value,
                            })
                          }
                          className="border p-1 w-full"
                        />
                      ) : (
                        guest.passportNo
                      )}
                    </td>
                    <td className="p-2 border text-center">
                      {editingId === guest.Id ? (
                        <button
                          onClick={() => handleSaveEdit(guest.Id)}
                          className="text-green-600 font-bold"
                        >
                          Save
                        </button>
                      ) : (
                        <button
                          onClick={() => handleEditClick(guest)}
                          className="text-blue-600 font-bold"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <hr className="my-4" />

        <div className="flex justify-end">
          <button
            onClick={handleSelectedGuests}
            className="flex items-center gap-2 bg-primary text-white px-[2.5vw] xl:px-[1.5vw] py-[1vw] xl:py-[0.5vw] rounded font-redhatMedium"
          >
            ✓ Select
          </button>
        </div>
      </div>
    </div>
  );
};
